import { useState } from "react"
import { Link } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import { Zap, CheckCircle, SlashCircle01, ArrowUpRight } from "@untitledui/icons"
import { toast } from "sonner"
import { useTeam } from "@/lib/api/use-teams"
import { teamsApi } from "@/lib/api/teams"
import { queryKeys } from "@/lib/api/query-keys"
import { useInbox } from "@/lib/api/use-campaigns"
import { Roles } from "@/lib/team/roles"
import { useCurrentRole } from "@/lib/team/use-current-role"
import { useAuth } from "@/lib/auth/auth-context"
import type { TeamSettingsMetadata } from "@/lib/api/types"
import { cx } from "@/lib/utils/cx"

// "Marketing Supercharge" settings — ONLY the switch and the rules (quiet
// hours, STOP handling, do-not-contact). Results deliberately do NOT live
// here: campaign performance is the /marketing page (a settings screen
// enables a feature; a CRM reports a campaign — Anthony, Jul 28).
export function MarketingSuperchargeSettings({ teamId }: { teamId: string }) {
  const { data: team, isLoading } = useTeam(teamId)
  const qc = useQueryClient()
  const [saving, setSaving] = useState(false)
  // Same manager gate as Goals & rewards: an estimator can SEE the report and
  // the DNC ledger (useful), but pausing all follow-up or moving quiet hours
  // is a manager's call.
  const role = useCurrentRole()
  const { user } = useAuth()
  const canManage =
    !!user?.admin ||
    role === Roles.Director ||
    role === Roles.GeneralManager ||
    role === Roles.ServiceManager

  const meta = (team?.metadata ?? {}) as TeamSettingsMetadata
  // Absence of the key means "not yet turned off" — default ON.
  const enabled = meta.marketingSupercharge?.enabled ?? true
  const quietStart = meta.marketingSupercharge?.quietStartHour ?? 8
  const quietEnd = meta.marketingSupercharge?.quietEndHour ?? 20
  // The do-not-contact ledger — who opted out, when, and why.
  const { data: inboxRows = [] } = useInbox(teamId)
  const dncRows = inboxRows.filter((r) => r.consentState === "opted_out")

  const cadence = {
    callEveryDays: meta.followUpCadence?.callEveryDays ?? 2,
    maxCalls: meta.followUpCadence?.maxCalls ?? 4,
  }
  const saveCadence = async (patch: Partial<typeof cadence>) => {
    if (!team || saving) return
    setSaving(true)
    try {
      await teamsApi.updateSettings(Number(teamId), {
        metadata: {
          ...(team.metadata as TeamSettingsMetadata),
          followUpCadence: { ...cadence, ...patch },
        },
      })
      await qc.invalidateQueries({ queryKey: queryKeys.teams.all })
      toast.success("Follow-up cadence updated")
    } catch {
      toast.error("Couldn't save that.")
    } finally {
      setSaving(false)
    }
  }

  const saveConfig = async (patch: Partial<TeamSettingsMetadata["marketingSupercharge"]>) => {
    if (!team || saving) return
    setSaving(true)
    try {
      await teamsApi.updateSettings(Number(teamId), {
        metadata: {
          ...(team.metadata as TeamSettingsMetadata),
          marketingSupercharge: {
            enabled,
            autoApproveClean: meta.marketingSupercharge?.autoApproveClean ?? false,
            enabledAt: meta.marketingSupercharge?.enabledAt ?? null,
            quietStartHour: quietStart,
            quietEndHour: quietEnd,
            ...patch,
          },
        },
      })
      qc.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) })
      qc.invalidateQueries({ queryKey: queryKeys.customerActivity.all })
      qc.invalidateQueries({ queryKey: queryKeys.campaigns.all })
      toast.success("Quiet hours updated")
    } catch {
      toast.error("Couldn't update. Try again.")
    } finally {
      setSaving(false)
    }
  }

  const setEnabled = async (next: boolean) => {
    if (!team || saving) return
    setSaving(true)
    try {
      // Read-modify-write — preserve every other metadata block (fees, goals,
      // service-lane…). goals-rewards sends only its own key today, which would
      // clobber this one on a replace-style BE; we spread to be safe.
      await teamsApi.updateSettings(Number(teamId), {
        metadata: {
          ...(team.metadata as TeamSettingsMetadata),
          marketingSupercharge: {
            enabled: next,
            autoApproveClean: meta.marketingSupercharge?.autoApproveClean ?? false,
            enabledAt: next
              ? new Date().toISOString()
              : (meta.marketingSupercharge?.enabledAt ?? null),
            quietStartHour: quietStart,
            quietEndHour: quietEnd,
          },
        },
      })
      qc.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) })
      toast.success(next ? "Marketing Supercharge on" : "Marketing Supercharge paused")
    } catch {
      toast.error("Couldn't update. Try again.")
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return <div className="h-48 rounded-2xl bg-white border border-zinc-200/70 animate-pulse" />
  }

  return (
    <div className="space-y-5 max-w-3xl">
      {/* The dark band — identity, the one switch, and the headline receipts. */}
      <div className="rounded-2xl bg-zinc-900 text-white shadow-xl shadow-zinc-900/10 overflow-hidden">
        <div className="px-5 md:px-7 py-5 md:py-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="size-11 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center shrink-0 shadow-lg shadow-emerald-500/25">
                <Zap className="size-5 text-white" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                  Marketing Supercharge
                </p>
                <h2 className="text-[18px] font-bold text-white leading-tight">
                  Automated follow-up
                </h2>
              </div>
            </div>
            <MasterToggle on={enabled} disabled={saving || !canManage} onChange={setEnabled} />
          </div>
          <p className="mt-3 text-[13px] text-zinc-400 leading-relaxed max-w-lg">
            When an estimate moves to In Progress, we follow up with the customer by text and
            email for 5 days — with their booking link — until they book or opt out.
            It's on and set up for you; there's nothing to configure.
          </p>
          {!canManage && (
            <p className="mt-2 text-[11.5px] text-zinc-500">
              Only a manager can change these settings.
            </p>
          )}
        </div>

        {/* Results live on the Marketing page — settings only points there. */}
        <Link
          to="/marketing"
          className="border-t border-white/10 px-5 md:px-7 py-3.5 flex items-center justify-between gap-3 text-[13px] font-semibold text-zinc-300 hover:text-white transition-colors group"
        >
          View campaign results
          <ArrowUpRight
            className="size-4 text-zinc-500 group-hover:text-white transition-colors"
            aria-hidden
          />
        </Link>
      </div>

      {/* Compliance — the rules that make automated messaging safe to run on
          real customers. Quiet hours are the shop's one lever; STOP handling
          and the do-not-contact ledger are shown, not configured. */}
      <div className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm divide-y divide-zinc-100 overflow-hidden">
        <section className="p-5 md:p-6">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
            Quiet hours
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-2">
            <p className="text-[13px] text-zinc-700">Messages only go out between</p>
            <HourSelect
              value={quietStart}
              options={[6, 7, 8, 9, 10, 11]}
              disabled={saving || !canManage}
              label="Quiet hours start"
              onChange={(h) => saveConfig({ quietStartHour: h })}
            />
            <p className="text-[13px] text-zinc-700">and</p>
            <HourSelect
              value={quietEnd}
              options={[17, 18, 19, 20, 21, 22]}
              disabled={saving || !canManage}
              label="Quiet hours end"
              onChange={(h) => saveConfig({ quietEndHour: h })}
            />
          </div>
          <p className="mt-2.5 text-[12px] text-zinc-500 leading-snug max-w-md">
            Anything the system would send outside this window — including a
            promotion you write at night — waits and goes out at{" "}
            {hourLabel(quietStart)} instead.
          </p>
        </section>

        {/* The task formula's numbers. "Every other day" is one shop's answer,
            so it's a setting, not a constant — the engine reads it live. */}
        <section className="p-5 md:p-6">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
            Follow-up calls
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-2">
            <p className="text-[13px] text-zinc-700">Ask for a call every</p>
            <NumberSelect
              value={cadence.callEveryDays}
              options={[1, 2, 3]}
              unit={(n) => (n === 1 ? "business day" : "business days")}
              disabled={saving || !canManage}
              label="Days between calls"
              onChange={(n) => saveCadence({ callEveryDays: n })}
            />
            <p className="text-[13px] text-zinc-700">and after</p>
            <NumberSelect
              value={cadence.maxCalls}
              options={[3, 4, 5, 6]}
              unit={() => "unanswered calls"}
              disabled={saving || !canManage}
              label="Calls before close-out"
              onChange={(n) => saveCadence({ maxCalls: n })}
            />
            <p className="text-[13px] text-zinc-700">ask whether to close it.</p>
          </div>
          <p className="mt-2.5 text-[12px] text-zinc-500 leading-snug max-w-md">
            Calls sit between the automatic texts and emails. A no-answer schedules
            the next call on this cadence; a callback the customer asks for pauses
            the texts until that day.
          </p>
        </section>

        <section className="p-5 md:p-6">
          <div className="flex items-start gap-2.5">
            <SlashCircle01 className="size-4 text-zinc-400 shrink-0 mt-0.5" aria-hidden />
            <div>
              <p className="text-[13px] font-semibold text-zinc-800">
                Opting out is instant and automatic
              </p>
              <p className="mt-1 text-[12.5px] text-zinc-500 leading-snug max-w-md">
                Every promotion carries &ldquo;Reply STOP to opt out.&rdquo; A customer
                who texts STOP is dropped from everything immediately and gets one
                confirmation — texting START brings them back. Nothing to set up.
              </p>
            </div>
          </div>
        </section>

        <section className="p-5 md:p-6">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
            Do not contact{dncRows.length > 0 ? ` · ${dncRows.length}` : ""}
          </p>
          {dncRows.length === 0 ? (
            <p className="mt-2.5 text-[12.5px] text-zinc-400">No one has opted out.</p>
          ) : (
            <div className="mt-2 divide-y divide-zinc-50">
              {dncRows.map((r) => (
                <div
                  key={r.customerId}
                  className="py-2.5 first:pt-1 last:pb-0 flex items-baseline justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-zinc-800 truncate">
                      {r.name}
                    </p>
                    {r.consentReason && (
                      <p className="text-[11.5px] text-zinc-400 mt-0.5">{r.consentReason}</p>
                    )}
                  </div>
                  <p className="text-[11.5px] text-zinc-400 tabular-nums shrink-0">
                    {r.consentUpdatedAt
                      ? new Date(r.consentUpdatedAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })
                      : ""}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* The promise — always visible; the only content before the first confirm. */}
      <div className="rounded-2xl border border-zinc-200/80 bg-white p-5 md:p-6 grid gap-2">
        <Point text="Pre-built, text-first 5-day sequence with the customer's booking link — proven to convert." />
        <Point text="Every reply lands in the Inbox and the customer's card, ready for a one-tap answer." />
        <Point text="Anyone handled offline or opted out is dropped automatically — no spam, ever." />
      </div>

      {!enabled && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-[13px] text-amber-900 leading-snug">
          Follow-up is <span className="font-semibold">paused</span> for this shop —
          no automated texts or emails will go out to any customer until you turn it
          back on.
        </div>
      )}
    </div>
  )
}

const hourLabel = (h: number) =>
  h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`

function HourSelect({
  value,
  options,
  disabled,
  label,
  onChange,
}: {
  value: number
  options: number[]
  disabled?: boolean
  label: string
  onChange: (h: number) => void
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(e) => onChange(Number(e.target.value))}
      className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-[13px] font-semibold text-zinc-800 hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 disabled:opacity-50"
    >
      {options.map((h) => (
        <option key={h} value={h}>
          {hourLabel(h)}
        </option>
      ))}
    </select>
  )
}

function Point({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2">
      <CheckCircle className="size-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden />
      <p className="text-[13px] text-zinc-600 leading-snug">{text}</p>
    </div>
  )
}

function MasterToggle({
  on,
  disabled,
  onChange,
}: {
  on: boolean
  disabled?: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label="Marketing Supercharge automated follow-up"
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={cx(
        // The visual pill stays 24px, but before:-inset-2.5 gives it a ~44px
        // invisible hit area without shifting layout (F37).
        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 disabled:opacity-50",
        "before:absolute before:-inset-2.5 before:content-['']",
        on ? "bg-emerald-500" : "bg-zinc-600",
      )}
    >
      <span
        className={cx(
          "inline-block size-5 rounded-full bg-white shadow transition-transform",
          on ? "translate-x-[22px]" : "translate-x-0.5",
        )}
      />
    </button>
  )
}


function NumberSelect({
  value,
  options,
  unit,
  disabled,
  label,
  onChange,
}: {
  value: number
  options: number[]
  unit: (n: number) => string
  disabled?: boolean
  label: string
  onChange: (n: number) => void
}) {
  return (
    <select
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-9 rounded-lg border border-zinc-200 bg-white px-2.5 text-[13px] font-semibold text-zinc-800 disabled:opacity-50"
    >
      {options.map((n) => (
        <option key={n} value={n}>
          {n} {unit(n)}
        </option>
      ))}
    </select>
  )
}
